import { useMsal } from '@azure/msal-react';
import { useState, useEffect, useRef } from 'react';
import { apiScope } from '../auth/msalConfig';

export type Role = 'Admin' | 'Editor' | 'Reader' | 'None';

const API_BASE = import.meta.env.VITE_API_BASE_URL || '/api/admin';

export function useRole(): Role | null {
  const { instance, accounts } = useMsal();
  const account = accounts[0];
  const [role, setRole] = useState<Role | null>(null);
  const attempted = useRef(false);

  useEffect(() => {
    if (!account) { setRole('None'); return; }
    if (attempted.current) return;
    attempted.current = true;

    // Get token manually then call /me
    instance.acquireTokenSilent({ scopes: [apiScope], account })
      .then(async (tokenResp) => {
        const res = await fetch(`${API_BASE}/me`, {
          headers: { Authorization: `Bearer ${tokenResp.accessToken}` },
        });
        if (!res.ok) {
          setRole('None');
          return;
        }
        const contentType = res.headers.get('content-type') || '';
        if (!contentType.toLowerCase().includes('application/json')) {
          const body = await res.text().catch(() => '');
          setRole('None');
          return;
        }
        const json = await res.json();
        setRole(json?.data?.role || 'None');
      })
      .catch((err) => {
        setRole('None');
      });
  }, [account, instance]);

  return role;
}

export function hasMinimumRole(userRole: Role | null, requiredRole: Role): boolean {
  if (userRole === null) return false;
  const hierarchy: Record<Role, number> = {
    None: 0,
    Reader: 1,
    Editor: 2,
    Admin: 3,
  };
  return hierarchy[userRole] >= hierarchy[requiredRole];
}
